import {
    ChangeDetectorRef,
    Component,
    Inject,
    LOCALE_ID,
    OnInit,
} from '@angular/core';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { EcmrActionService } from '@app/core/services/ecmr-services/ecmr-action.service';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { Router } from '@angular/router';
import { EcmrTransportType } from '@app/shared/models/ecmr/EcmrTransportType';
import { formatDate } from '@angular/common';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { EcmrEditorService } from '@app/core/services/ecmr-services/ecmr-editor.service';
import { EcmrShareDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-share-dialog/ecmr-share-dialog.component';
import { TemplateUser } from '@app/shared/models/ecmr/TemplateUser';
import { SealMetadata } from '@app/shared/models/ecmr/SealMetadata';
import { lastValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { EcmrDocumentService } from '@app/core/services/ecmr-services/ecmr-document.service';
import { EcmrDocument } from '@app/shared/models/ecmr/EcmrDocument';
import { EcmrStatusComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-status/ecmr-status.component';
import { EcmrDisplayInformationFieldComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-display-information-field/ecmr-display-information-field.component';
import { LatestMotorPlatePipe } from '@app/shared/pipes/latest-motor-plate.pipe';
import { isEcmrDcaCompliant } from '@app/shared/components/ecmr/ecmr-dca.util';

@Component({
    selector: 'app-ecmr-detail-dialog',
    templateUrl: './ecmr-detail-dialog.component.html',
    styleUrl: './ecmr-detail-dialog.component.scss',
    imports: [
        CommonModule,
        MaterialModule,
        TranslateModule,
        EcmrStatusComponent,
        EcmrDisplayInformationFieldComponent,
        LatestMotorPlatePipe,
    ],
})
export class EcmrDetailDialogComponent implements OnInit {
    baseRoute: string;
    isTemplate: boolean = false;
    isArchive: boolean = false;
    ecmr!: Ecmr;
    template: TemplateUser;
    isShareButtonDisabled: boolean = false;
    isReadOnly: boolean = false;
    sealMetadata: SealMetadata[] = [];
    documents: EcmrDocument[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            baseRoute: string;
            ecmr: Ecmr;
            template: TemplateUser;
            isArchive: boolean;
            isReadOnly: boolean;
        },
        @Inject(LOCALE_ID) public locale: string,
        private dialogRef: MatDialogRef<EcmrDetailDialogComponent>,
        protected ecmrActionsService: EcmrActionService,
        private router: Router,
        private ecmrService: EcmrService,
        private ecmrEditorService: EcmrEditorService,
        private dialog: MatDialog,
        private toastr: ToastrService,
        private translasteService: TranslateService,
        private documentService: EcmrDocumentService,
        private cd: ChangeDetectorRef
    ) {
        this.baseRoute = data.baseRoute;
        this.ecmr = data.ecmr;
        this.isTemplate = data.template !== undefined;
        if (this.isTemplate) {
            this.template = data.template;
        }
        this.isArchive = data.isArchive;
        this.isReadOnly = data.isReadOnly;
    }

    ngOnInit() {
        if (this.ecmr?.ecmrId) {
            this.ecmrEditorService
                .getSealMetadata(this.ecmr.ecmrId)
                .subscribe(metadata => {
                    this.sealMetadata = metadata;
                    // Seal metadata arrives asynchronously and toggles the
                    // signature @if blocks; run change detection so the new
                    // value is picked up in a clean pass (avoids NG0100).
                    this.cd.detectChanges();
                });
            this.documentService
                .listDocuments(this.ecmr.ecmrId)
                .subscribe(docs => {
                    this.documents = docs;
                    this.cd.detectChanges();
                });
        }
    }

    get senderSealMetadata(): SealMetadata | undefined {
        return this.sealMetadata.find(s => s.role === 'SENDER');
    }

    /** DeCA note shows only once the sender has signed and the tax numbers make it conform. */
    get dcaCompliant(): boolean {
        return (
            !!this.senderSealMetadata &&
            isEcmrDcaCompliant(this.ecmr.ecmrConsignment)
        );
    }

    get carrierSealMetadata(): SealMetadata | undefined {
        return this.sealMetadata.find(s => s.role === 'CARRIER');
    }

    get consigneeSealMetadata(): SealMetadata | undefined {
        return this.sealMetadata.find(s => s.role === 'CONSIGNEE');
    }

    close() {
        this.dialogRef.close();
    }

    toDateString(logisticsTimeOfArrivalDateTime: Date | string | null | undefined) {
        if (logisticsTimeOfArrivalDateTime)
            return formatDate(
                logisticsTimeOfArrivalDateTime,
                'dd.MM.yyyy HH:mm',
                this.locale
            );
        else return '';
    }

    public getTransportType(ecmr: Ecmr): EcmrTransportType | null {
        return this.ecmrService.getTransportType(ecmr);
    }

    editEcmr() {
        if (this.ecmr) {
            if (this.isTemplate) {
                this.dialogRef.close();
                this.router.navigate([
                    this.baseRoute +
                        '/' +
                        RoutingEndpoints.ecmr_template_editor +
                        `/${this.template.id}`,
                ]);
            } else {
                this.dialogRef.close();
                this.router.navigate([
                    this.baseRoute +
                        '/' +
                        RoutingEndpoints.ecmr_editor +
                        `/${this.ecmr.ecmrId}`,
                ]);
            }
        }
    }

    async moveToArchive() {
        try {
            await lastValueFrom(
                this.ecmrService.moveToArchive(this.ecmr.ecmrId!)
            );
            this.dialogRef.close(true);
            this.toastr.success(
                this.translasteService.instant(
                    'ECMR.archive.successfully_moved_to_archive'
                )
            );
        } catch (e) {
            this.toastr.error(
                this.translasteService.instant(
                    'ECMR.archive.error_moved_to_archive'
                )
            );
        }
    }

    async reactivateFromArchive() {
        try {
            await lastValueFrom(
                this.ecmrService.moveOutOfArchive(this.ecmr.ecmrId!)
            );
            this.dialogRef.close(true);
            this.toastr.success(
                this.translasteService.instant(
                    'ECMR.archive.move_to_overview_success'
                )
            );
        } catch (e) {
            this.toastr.error(
                this.translasteService.instant(
                    'ECMR.archive.move_to_overview_error'
                )
            );
        }
    }

    showHistoryOfEcmr() {
        if (this.ecmr) {
            this.close();

            this.router.navigate([
                this.baseRoute +
                    '/' +
                    RoutingEndpoints.ecmr_history +
                    `/${this.ecmr.ecmrId}/${
                        this.ecmr.ecmrConsignment.referenceIdentificationNumber
                            .value
                    }`,
            ]);
        }
    }

    shareEcmr() {
        this.close();
        const dialogRef = this.dialog.open(EcmrShareDialogComponent, {
            width: '70vw',
            data: {
                ecmr: this.ecmr,
            },
        });
    }

    downloadDocument(doc: EcmrDocument): void {
        this.documentService.downloadDocument(doc.id).subscribe({
            next: (response) => {
                if (response.body) {
                    const blob = new Blob([response.body], {
                        type: 'application/octet-stream',
                    });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = doc.fileName;
                    link.click();
                    URL.revokeObjectURL(url);
                }
            },
            error: () => {
                this.toastr.error(
                    this.translasteService.instant(
                        'ECMR.ecmr_editor.documents.error_download_failed'
                    )
                );
            },
        });
    }

    getFileIcon(mimeType: string): string {
        if (mimeType === 'application/pdf') {
            return 'picture_as_pdf';
        }
        return 'image';
    }

    protected readonly EcmrStatus = EcmrStatus;
    protected readonly EcmrTransportType = EcmrTransportType;
}
